import React, { memo, Component } from 'react';
import { motion } from 'framer-motion';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Truck, MapPin, Clock, Package, CheckCircle, AlertCircle, Phone, MessageCircle, Calendar, DollarSign } from 'lucide-react';
import { Button } from '../components/ui/Button';
const WHATSAPP_NUMBER = '27732736906';
export function DeliveryInfoPage() {
  const handleWhatsAppContact = () => {
    const message = `Hi! I have a question about delivery.`;
    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodedMessage}`;
    window.open(whatsappUrl, '_blank');
  };
  return <div className="min-h-screen bg-white" data-id="element-2153">
      <Navbar data-id="element-2154" />

      <main className="pt-24 pb-16" data-id="element-2155">
        {/* Hero Section */}
        <section className="bg-gradient-to-b from-rose-50 to-white py-16" data-id="element-2156">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2157">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6
          }} data-id="element-2158">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-rose-100 rounded-full mb-6" data-id="element-2159">
                <Truck className="w-8 h-8 text-rose-600" data-id="element-2160" />
              </div>
              <h1 className="font-serif text-4xl md:text-5xl font-bold text-gray-900 mb-4" data-id="element-2161">
                Delivery Information
              </h1>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto" data-id="element-2162">
                Fast, reliable delivery to make your special moments even more
                memorable
              </p>
            </motion.div>
          </div>
        </section>

        {/* Delivery Pricing */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" data-id="element-2163">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2
        }} className="text-center mb-12" data-id="element-2164">
            <h2 className="font-serif text-3xl font-bold text-gray-900 mb-4" data-id="element-2165">
              Delivery Rates
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto" data-id="element-2166">
              Transparent pricing with no hidden fees
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto mb-16" data-id="element-2167">
            {/* Within Heidelberg */}
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: 0.3
          }} className="bg-white rounded-2xl p-8 shadow-lg border-2 border-rose-200 relative overflow-hidden" data-id="element-2168">
              <div className="absolute top-0 right-0 w-32 h-32 bg-rose-100 rounded-full -mr-16 -mt-16 opacity-50" data-id="element-2169" />
              <div className="relative" data-id="element-2170">
                <div className="w-12 h-12 bg-rose-100 rounded-full flex items-center justify-center mb-4" data-id="element-2171">
                  <MapPin className="w-6 h-6 text-rose-600" data-id="element-2172" />
                </div>
                <h3 className="font-bold text-2xl text-gray-900 mb-2" data-id="element-2173">
                  Within Heidelberg
                </h3>
                <p className="text-gray-600 mb-6" data-id="element-2174">
                  Delivery within 15km radius of Heidelberg
                </p>
                <div className="flex items-baseline gap-2 mb-6" data-id="element-2175">
                  <span className="text-5xl font-bold text-rose-600" data-id="element-2176">R75</span>
                  <span className="text-gray-500" data-id="element-2177">per delivery</span>
                </div>
                <ul className="space-y-3" data-id="element-2178">
                  <li className="flex items-start gap-3" data-id="element-2179">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" data-id="element-2180" />
                    <span className="text-gray-700" data-id="element-2181">
                      Same-day delivery available
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-2182">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" data-id="element-2183" />
                    <span className="text-gray-700" data-id="element-2184">Scheduled time slots</span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-2185">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" data-id="element-2186" />
                    <span className="text-gray-700" data-id="element-2187">
                      Contact-free delivery option
                    </span>
                  </li>
                </ul>
              </div>
            </motion.div>

            {/* Outside Heidelberg */}
            <motion.div initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: 0.4
          }} className="bg-white rounded-2xl p-8 shadow-lg border-2 border-purple-200 relative overflow-hidden" data-id="element-2188">
              <div className="absolute top-0 right-0 w-32 h-32 bg-purple-100 rounded-full -mr-16 -mt-16 opacity-50" data-id="element-2189" />
              <div className="relative" data-id="element-2190">
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mb-4" data-id="element-2191">
                  <Truck className="w-6 h-6 text-purple-600" data-id="element-2192" />
                </div>
                <h3 className="font-bold text-2xl text-gray-900 mb-2" data-id="element-2193">
                  Outside Heidelberg
                </h3>
                <p className="text-gray-600 mb-6" data-id="element-2194">
                  Delivery beyond 15km radius
                </p>
                <div className="flex items-baseline gap-2 mb-6" data-id="element-2195">
                  <span className="text-5xl font-bold text-purple-600" data-id="element-2196">
                    R150
                  </span>
                  <span className="text-gray-500" data-id="element-2197">per delivery</span>
                </div>
                <ul className="space-y-3" data-id="element-2198">
                  <li className="flex items-start gap-3" data-id="element-2199">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" data-id="element-2200" />
                    <span className="text-gray-700" data-id="element-2201">
                      Extended delivery area
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-2202">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" data-id="element-2203" />
                    <span className="text-gray-700" data-id="element-2204">Flexible scheduling</span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-2205">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" data-id="element-2206" />
                    <span className="text-gray-700" data-id="element-2207">
                      Careful handling guaranteed
                    </span>
                  </li>
                </ul>
              </div>
            </motion.div>
          </div>

          {/* After Hours Notice */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.5
        }} className="max-w-4xl mx-auto mb-16" data-id="element-2208">
            <div className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-2xl p-6 border border-yellow-200" data-id="element-2209">
              <div className="flex items-start gap-4" data-id="element-2210">
                <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-2211">
                  <Clock className="w-5 h-5 text-yellow-700" data-id="element-2212" />
                </div>
                <div data-id="element-2213">
                  <h3 className="font-bold text-gray-900 mb-2" data-id="element-2214">
                    After Hours Delivery
                  </h3>
                  <p className="text-gray-700 mb-2" data-id="element-2215">
                    Deliveries scheduled after 19:00 (7 PM) incur{' '}
                    <strong data-id="element-2216">double the standard delivery fee</strong>.
                  </p>
                  <ul className="text-sm text-gray-600 space-y-1" data-id="element-2217">
                    <li data-id="element-2218">
                      • Within Heidelberg after 7 PM: <strong data-id="element-2219">R150</strong>
                    </li>
                    <li data-id="element-2220">
                      • Outside Heidelberg after 7 PM: <strong data-id="element-2221">R300</strong>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </motion.div>

          {/* How It Works */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.6
        }} className="max-w-4xl mx-auto" data-id="element-2222">
            <h2 className="font-serif text-3xl font-bold text-gray-900 mb-8 text-center" data-id="element-2223">
              How Delivery Works
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6" data-id="element-2224">
              <div className="text-center" data-id="element-2225">
                <div className="w-16 h-16 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-2226">
                  <Package className="w-8 h-8 text-rose-600" data-id="element-2227" />
                </div>
                <h3 className="font-bold text-gray-900 mb-2" data-id="element-2228">1. Place Order</h3>
                <p className="text-sm text-gray-600" data-id="element-2229">
                  Choose your items and add delivery details
                </p>
              </div>

              <div className="text-center" data-id="element-2230">
                <div className="w-16 h-16 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-2231">
                  <Calendar className="w-8 h-8 text-rose-600" data-id="element-2232" />
                </div>
                <h3 className="font-bold text-gray-900 mb-2" data-id="element-2233">
                  2. Select Date & Time
                </h3>
                <p className="text-sm text-gray-600" data-id="element-2234">
                  Pick your preferred delivery slot
                </p>
              </div>

              <div className="text-center" data-id="element-2235">
                <div className="w-16 h-16 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-2236">
                  <CheckCircle className="w-8 h-8 text-rose-600" data-id="element-2237" />
                </div>
                <h3 className="font-bold text-gray-900 mb-2" data-id="element-2238">
                  3. Confirmation
                </h3>
                <p className="text-sm text-gray-600" data-id="element-2239">
                  Receive order confirmation via email
                </p>
              </div>

              <div className="text-center" data-id="element-2240">
                <div className="w-16 h-16 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-2241">
                  <Truck className="w-8 h-8 text-rose-600" data-id="element-2242" />
                </div>
                <h3 className="font-bold text-gray-900 mb-2" data-id="element-2243">4. Delivery</h3>
                <p className="text-sm text-gray-600" data-id="element-2244">
                  Your gift arrives on time, perfectly packaged
                </p>
              </div>
            </div>
          </motion.div>
        </section>

        {/* Delivery Areas */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto bg-gray-50" data-id="element-2245">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.7
        }} data-id="element-2246">
            <h2 className="font-serif text-3xl font-bold text-gray-900 mb-8 text-center" data-id="element-2247">
              Delivery Areas
            </h2>
            <div className="max-w-4xl mx-auto bg-white rounded-2xl p-8 shadow-sm border border-gray-200" data-id="element-2248">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-2249">
                <div data-id="element-2250">
                  <h3 className="font-bold text-xl text-gray-900 mb-4 flex items-center gap-2" data-id="element-2251">
                    <MapPin className="w-5 h-5 text-rose-600" data-id="element-2252" />
                    Within Heidelberg (R75)
                  </h3>
                  <ul className="space-y-2 text-gray-700" data-id="element-2253">
                    <li data-id="element-2254">• Heidelberg CBD</li>
                    <li data-id="element-2255">• Heidelberg Ext 1-10</li>
                    <li data-id="element-2256">• Surrounding suburbs within 15km</li>
                    <li data-id="element-2257">• Industrial areas</li>
                  </ul>
                </div>

                <div data-id="element-2258">
                  <h3 className="font-bold text-xl text-gray-900 mb-4 flex items-center gap-2" data-id="element-2259">
                    <Truck className="w-5 h-5 text-purple-600" data-id="element-2260" />
                    Outside Heidelberg (R150)
                  </h3>
                  <ul className="space-y-2 text-gray-700" data-id="element-2261">
                    <li data-id="element-2262">• Ratanda</li>
                    <li data-id="element-2263">• Surrounding farms</li>
                    <li data-id="element-2264">• Neighboring towns</li>
                    <li data-id="element-2265">• Areas beyond 15km radius</li>
                  </ul>
                </div>
              </div>

              <div className="mt-8 p-4 bg-blue-50 rounded-xl border border-blue-200" data-id="element-2266">
                <p className="text-sm text-blue-900 flex items-start gap-2" data-id="element-2267">
                  <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" data-id="element-2268" />
                  <span data-id="element-2269">
                    Not sure which zone you're in? Contact us and we'll help you
                    determine the delivery fee for your area.
                  </span>
                </p>
              </div>
            </div>
          </motion.div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-2270">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.8
        }} data-id="element-2271">
            <h2 className="font-serif text-3xl font-bold text-gray-900 mb-8 text-center" data-id="element-2272">
              Frequently Asked Questions
            </h2>
            <div className="space-y-4" data-id="element-2273">
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200" data-id="element-2274">
                <h3 className="font-bold text-gray-900 mb-2" data-id="element-2275">
                  Do you offer same-day delivery?
                </h3>
                <p className="text-gray-700" data-id="element-2276">
                  Yes! Same-day delivery is available for orders placed before 2
                  PM, subject to availability and delivery area.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200" data-id="element-2277">
                <h3 className="font-bold text-gray-900 mb-2" data-id="element-2278">
                  Can I schedule a specific delivery time?
                </h3>
                <p className="text-gray-700" data-id="element-2279">
                  Absolutely! During checkout, you can select your preferred
                  delivery date and time slot. We'll do our best to accommodate
                  your request.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200" data-id="element-2280">
                <h3 className="font-bold text-gray-900 mb-2" data-id="element-2281">
                  What if I need to change my delivery address?
                </h3>
                <p className="text-gray-700" data-id="element-2282">
                  Contact us as soon as possible via WhatsApp or phone. We'll
                  update your delivery details if the order hasn't been
                  dispatched yet.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200" data-id="element-2283">
                <h3 className="font-bold text-gray-900 mb-2" data-id="element-2284">
                  Is there a minimum order for delivery?
                </h3>
                <p className="text-gray-700" data-id="element-2285">
                  No minimum order required! We deliver orders of any size, with
                  the standard delivery fee applied based on your location.
                </p>
              </div>
            </div>
          </motion.div>
        </section>

        {/* Contact CTA */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-2286">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.9
        }} className="bg-gradient-to-br from-green-500 to-green-600 rounded-2xl p-8 text-white text-center" data-id="element-2287">
            <h2 className="font-bold text-2xl mb-4" data-id="element-2288">
              Have Questions About Delivery?
            </h2>
            <p className="text-green-50 mb-6 max-w-2xl mx-auto" data-id="element-2289">
              Our team is here to help! Contact us via WhatsApp or phone for
              instant answers about delivery options, pricing, or scheduling.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-2290">
              <Button onClick={handleWhatsAppContact} className="bg-white text-green-600 hover:bg-green-50" leftIcon={<MessageCircle className="w-5 h-5" data-id="element-2292" />} data-id="element-2291">
                WhatsApp Us
              </Button>
              <Button onClick={() => window.location.href = 'tel:0732736906'} className="bg-white text-green-600 hover:bg-green-50" leftIcon={<Phone className="w-5 h-5" data-id="element-2294" />} data-id="element-2293">
                Call Us
              </Button>
            </div>
          </motion.div>
        </section>
      </main>

      <Footer data-id="element-2295" />
    </div>;
}